import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import './Dashboard.css';

const EDIT_FIELDS = [
  'value',
  'part_of_speech',
  'meaning_0',
  'meaning_1',
  'meaning_2',
  'synonym_0',
  'synonym_1',
  'synonym_2',
  'synonym_3',
  'synonym_4',
  'synonym_5',
  'tone',
];

const cellInputStyle = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 13,
  minWidth: 56,
  width: '100%',
  boxSizing: 'border-box',
};

const Word = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  const [rows, setRows] = useState([]);
  const [fromDb, setFromDb] = useState(false);
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) {
          setPassages(res.data.passages || []);
          setPassagesError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEditor = useCallback(async (pid) => {
    const res = await api.get('/data/words/editor', { params: { passageId: pid } });
    setFromDb(Boolean(res.data?.fromDb));
    setRows(res.data?.rows || []);
  }, []);

  useEffect(() => {
    if (!passageId) {
      setRows([]);
      setFromDb(false);
      setLoadError('');
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoadLoading(true);
      setLoadError('');
      setMessage({ type: '', text: '' });
      try {
        await loadEditor(parseInt(passageId, 10));
        if (!cancelled) setLoadError('');
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setLoadError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load words'
          );
        }
      } finally {
        if (!cancelled) setLoadLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [passageId, loadEditor]);

  const updateCell = useCallback((index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const performSaveWords = async () => {
    setMessage({ type: '', text: '' });
    if (!passageId) {
      setMessage({ type: 'error', text: 'Select a passage.' });
      return false;
    }
    if (rows.length === 0) {
      setMessage({
        type: 'error',
        text: fromDb
          ? 'No word rows to save.'
          : 'No rows to save. Add short sentences first, or passage has no splittable text.',
      });
      return false;
    }

    setSubmitLoading(true);
    try {
      const res = await api.post('/data/words/save-for-passage', {
        passageId: parseInt(passageId, 10),
        rows: rows.map((r) => ({
          short_sentence_id: r.short_sentence_id,
          value: r.value,
          part_of_speech: r.part_of_speech,
          meaning_0: r.meaning_0,
          meaning_1: r.meaning_1,
          meaning_2: r.meaning_2,
          tone: r.tone,
          synonym_0: r.synonym_0,
          synonym_1: r.synonym_1,
          synonym_2: r.synonym_2,
          synonym_3: r.synonym_3,
          synonym_4: r.synonym_4,
          synonym_5: r.synonym_5,
        })),
      });
      const n = res.data?.inserted;
      setMessage({
        type: 'success',
        text:
          n != null
            ? `${res.data?.message || 'Saved'} (${n} row(s))`
            : res.data?.message || 'Saved',
      });
      await loadEditor(parseInt(passageId, 10));
      setFromDb(true);
      return true;
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to save words',
      });
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await performSaveWords();
  };

  const handleSaveAndGoTranslationQuestion = async () => {
    const ok = await performSaveWords();
    if (ok && passageId) {
      navigate(`/translation-question?passageId=${encodeURIComponent(passageId)}`);
    }
  };

  const hasRows = rows.length > 0;

  return (
    <div className="dashboard">
      <DashboardHeader title="Word editor" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: '100%', margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Words</h2>

            <div className="form-group">
              <PassagePicker
                inputId="word-passage-picker"
                label={
                  <>
                    Passage <span className="required">*</span>
                  </>
                }
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={passageId}
                onChange={setPassageId}
              />
            </div>

            {passageId && loadLoading && <div className="loading-state">Loading…</div>}
            {passageId && !loadLoading && loadError && <div className="message error">{loadError}</div>}

            {passageId && !loadLoading && !loadError && (
              <>
                <p className="form-hint" style={{ marginBottom: 12 }}>
                  {fromDb
                    ? 'Loaded from word table. Order by word.order. word_id, order, and short_sentence_id are read-only.'
                    : 'No word rows yet — seeded from short_sentence (one grapheme per row). Assign short_sentence_id is set per row; save to persist.'}
                </p>

                {!hasRows && (
                  <div className="message error" style={{ marginBottom: 12 }}>
                    No rows. Add short sentences for this passage, then open this page again.
                  </div>
                )}

                {hasRows && (
                  <form onSubmit={handleSave}>
                    <div
                      className="word-editor-scroll"
                      style={{
                        height: '60vh',
                        overflow: 'auto',
                        marginBottom: 16,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        background: '#fff',
                      }}
                    >
                      <table
                        className="word-editor-table"
                        style={{
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                          width: 'max-content',
                          minWidth: '100%',
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr>
                            {['word_id', 'order', 'short_sentence_id', ...EDIT_FIELDS].map((col) => (
                              <th
                                key={col}
                                style={{
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 2,
                                  textAlign: 'left',
                                  padding: '8px 6px',
                                  borderBottom: '1px solid #bbb',
                                  whiteSpace: 'nowrap',
                                  background: '#e8e8e8',
                                  boxShadow: '0 1px 0 #bbb',
                                }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, index) => (
                            <tr key={`${row.short_sentence_id ?? 'x'}-${row.order ?? index}-${index}`}>
                              <td style={{ padding: 6, borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>
                                {row.word_id != null ? row.word_id : '—'}
                              </td>
                              <td style={{ padding: 6, borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>
                                {row.order != null ? row.order : index + 1}
                              </td>
                              <td style={{ padding: 6, borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>
                                {row.short_sentence_id != null ? row.short_sentence_id : '—'}
                              </td>
                              {EDIT_FIELDS.map((field) => (
                                <td key={field} style={{ padding: 4, borderBottom: '1px solid #eee' }}>
                                  <input
                                    type="text"
                                    value={row[field] == null ? '' : String(row[field])}
                                    onChange={(e) => updateCell(index, field, e.target.value)}
                                    disabled={submitLoading}
                                    style={cellInputStyle}
                                    aria-label={`${field} row ${index + 1}`}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <button type="submit" className="submit-button" disabled={submitLoading}>
                        {submitLoading
                          ? 'Saving…'
                          : fromDb
                            ? 'Save words'
                            : 'Insert words'}
                      </button>
                      <button
                        type="button"
                        className="submit-button"
                        disabled={submitLoading}
                        onClick={handleSaveAndGoTranslationQuestion}
                      >
                        {submitLoading
                          ? 'Saving…'
                          : fromDb
                            ? 'Save words and go to Translation question'
                            : 'Insert words and go to Translation question'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Word;
